import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Grid,
  Box,
  Avatar,
  Typography,
  TextField,
  FormControl,
  FormLabel,
  FormGroup,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Chip,
  Stack,
} from '@mui/material';
import { AddAPhoto as PhotoCamera } from '@mui/icons-material';

const certificationLabels = {
  dinghy_12: 'Dinghy CANSail 1,2',
  dinghy_34: 'Dinghy CANSail 3,4',
  dinghy_56: 'Dinghy CANSail 5,6',
  basic_crew: 'Basic Crew/Cruising',
  basic_keelboat: 'Basic Keelboat Racing',
  keelboat_spinnaker: 'Keelboat Spinnaker',
  catamaran: 'Catamaran Endorsment',
  intermediate_crew: 'Intermediate Crew/Cruising',
  advanced_crew: 'Advanced Crew/Cruising',
  offshore_crew: 'Offshore Crew/Cruising',
};

const ProfileEditorAboutYou = ({
  profilePicturePreview,
  isLoadingPhoto,
  handleProfilePictureChange,
  aboutYou,
  setAboutYou,
  phoneNumber,
  setPhoneNumber,
  certifications,
  handleCertificationChange,
  role,
  showInPublicRoster,
  setShowInPublicRoster,
  showPhoneInRoster,
  setShowPhoneInRoster,
  showEmailInRoster,
  setShowEmailInRoster,
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event) => {
    handleProfilePictureChange(event);
    // Reset the file input value so the same file can be selected again if needed
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={4}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 2 }}>
          <input
            accept="image/*"
            style={{ display: 'none' }}
            ref={fileInputRef}
            id="profile-picture-input"
            type="file"
            onChange={handleFileChange}
          />
          <Avatar
            src={profilePicturePreview}
            onClick={handleAvatarClick}
            sx={{
              width: 150,
              height: 150,
              cursor: 'pointer',
              '&:hover': {
                opacity: 0.8,
              },
            }}
          >
            {isLoadingPhoto ? (
              <CircularProgress />
            ) : (
              !profilePicturePreview && <PhotoCamera fontSize="large" />
            )}
          </Avatar>
          <Typography variant="caption" sx={{ mt: 1 }}>
            {t(`profile.aboutYou.profilePicture.${profilePicturePreview ? 'change' : 'select'}`)}
          </Typography>
        </Box>
      </Grid>
      <Grid item xs={12} sm={8}>
        <TextField
          fullWidth
          label={t('profile.aboutYou.title')}
          value={aboutYou}
          onChange={(e) => setAboutYou(e.target.value)}
          margin="normal"
          multiline
          rows={4}
          slotProps={{ htmlInput: { maxLength: 1000 } }}
        />
        <TextField
          fullWidth
          label={t('profile.aboutYou.phoneNumber')}
          value={phoneNumber}
          onChange={(e) => setPhoneNumber(e.target.value)}
          margin="normal"
          slotProps={{ input: { maxLength: 20 } }}
        />
      </Grid>
      {false && <Grid item xs={12}>
        <FormControl component="fieldset" margin="normal" fullWidth>
          <FormLabel component="legend">Certifications</FormLabel>
          <Grid container spacing={2}>
            {Object.entries(certificationLabels).map(([key, label]) => (
              <Grid item xs={12} sm={6} md={4} key={key}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={certifications[key] || false}
                      onChange={handleCertificationChange}
                      name={key}
                    />
                  }
                  label={label}
                />
              </Grid>
            ))}
          </Grid>
        </FormControl>
      </Grid>}
      
      {/* Public Roster Settings - Only for crew */}
      {role === 'crew' && (
        <Grid item xs={12}>
          <Box sx={{ mt: 3 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <Typography variant="h6">
                {t('profile.aboutYou.publicRoster')}
              </Typography>
              <Chip label={t('match.new')} color="primary" size="small" sx={{ fontWeight: 'bold' }} />
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('profile.aboutYou.publicRosterHint')}
            </Typography>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showInPublicRoster || false}
                    onChange={(e) => setShowInPublicRoster(e.target.checked)}
                    name="showInPublicRoster"
                  />
                }
                label={t('profile.aboutYou.showInPublicRoster')}
              />
              {showInPublicRoster && (
                <>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={showPhoneInRoster || false}
                        onChange={(e) => setShowPhoneInRoster(e.target.checked)}
                        name="showPhoneInRoster"
                      />
                    }
                    label={t('profile.aboutYou.showPhoneInRoster')}
                    sx={{ ml: 2 }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={showEmailInRoster || false}
                        onChange={(e) => setShowEmailInRoster(e.target.checked)}
                        name="showEmailInRoster"
                      />
                    }
                    label={t('profile.aboutYou.showEmailInRoster')}
                    sx={{ ml: 2 }}
                  />
                </>
              )}
            </FormGroup>
          </Box>
        </Grid>
      )}
    </Grid>
  );
};

export default ProfileEditorAboutYou;
